import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { KioskLogin } from './KioskLogin.jsx';
import { KioskShell } from './KioskShell.jsx';
import { isStale, liveRevision } from '../lib/kiosk-update.js';

const UPDATE_CHECK_MS = 5 * 60 * 1000;

export function KioskApp({ locationKey }) {
  const [session, setSession] = useState(null); // { token, user, location, idleLogoutMs }
  const [tab, setTab] = useState('tasks');
  const [bootError, setBootError] = useState('');

  // Idle auto-logout: any pointer/key/touch resets the timer; expiry kicks
  // back to the PIN pad.
  useEffect(() => {
    if (!session) return;
    let timer;
    function reset() {
      clearTimeout(timer);
      timer = setTimeout(() => setSession(null), session.idleLogoutMs || 60000);
    }
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [session]);

  // Pick up new deploys on their own: only while nobody is signed in, so a
  // reload never interrupts someone mid-task. A failed check just waits for
  // the next one.
  useEffect(() => {
    if (session) return;
    const check = () => liveRevision()
      .then((rev) => { if (isStale(window.__MONARCH_BUILD, rev)) window.location.reload(); })
      .catch(() => {});
    check();
    const timer = setInterval(check, UPDATE_CHECK_MS);
    return () => clearInterval(timer);
  }, [session]);

  // Validate the location at mount — bail with a clear error if the URL
  // doesn't match a known café key. Uses the public cafés endpoint
  // (`/api/applications/cafes-public`, also used by /apply) because the
  // umbrella requireAuth blocks `/api/locations` without a Bearer token,
  // and a kiosk has no Bearer until after PIN login. Calling the authed
  // endpoint here was the cause of the "kiosk shows Authentication
  // required" bug after the prebuilt SPA went 100% live (2026-05-07).
  useEffect(() => {
    api('GET', '/api/applications/cafes-public').then(r => {
      const locs = (r && r.cafes) || [];
      const match = locs.find(l => String(l.name).toLowerCase().replace(/\s+/g, '_') === locationKey);
      if (!match) setBootError(`Unknown location: ${locationKey}`);
    }).catch(e => setBootError(e.message || String(e)));
  }, []);

  if (bootError) {
    return (
      <div style={{ minHeight: '100vh', background: '#1c1f1d', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, color: '#b48a47', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Monarch Kiosk</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Setup error</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{bootError}</div>
        </div>
      </div>
    );
  }

  if (!session) return <KioskLogin locationKey={locationKey} onLogin={setSession} />;

  return <KioskShell session={session} tab={tab} setTab={setTab} onLogout={() => setSession(null)} />;
}
